import { MainButton } from "@/Components/MainButton"
import { useAppDispatch, useAppSelector } from "@/store/hooks"
import { setState, setModalData } from "@/store/globalSlice"
import { evaluateRequiredRollValues } from "@/utils/animationHelpers"
import { getUserCreds, isMobile, socket } from "@/utils/utils"
import classNames from "classnames"
import { useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useGameTimings } from "@/Hooks/useGameTimings"
import { images } from "@/utils/data"
import {
  playerHasAnyHouses,
  playerHasCompleteSet,
} from "@/utils/propmngHelpers"

interface GameButtonsProps {}

const GameButtons: React.FC<GameButtonsProps> = () => {
  const dispatch = useAppDispatch()
  const myGame = useAppSelector((state) => state.global.myGame)
  const modalData = useAppSelector((state) => state.global.modalData)
  const pendingDecision = useAppSelector((state) => state.global.pendingDecision)
  const houseBuildMode = useAppSelector((state) => state.global.houseBuildMode)
  const diceRolling = useAppSelector((state) => state.global.diceRolling)
  const pieceMovementAnimationRunning = useAppSelector(
    (state) => state.global.pieceMovementAnimationRunning
  )
  const disableEndTurnLocally = useAppSelector(
    (state) => state.global.disableEndTurnLocally
  )
  const cheats = useAppSelector((state) => state.global.cheats)
  const { turnTotalTime } = useGameTimings()
  const handleEndTurn = () => {
    dispatch(
      setState({ timerValue: turnTotalTime, disableEndTurnLocally: true })
    )
    socket.emit("game.endTurn", myGame?.id || "")
  }
  const [diceInputVal, setDiceInputVal] = useState("")
  const nav = useNavigate()

  const enterHouseBuildMode = (mode: "buy" | "sell") => {
    dispatch(setState({ houseBuildMode: mode }))
  }
  const me = myGame?.situation?.players.find(
    (player) => player.id === getUserCreds().email
  )
  const myOrder = me?.order
  const myCardsCount = me?.cards.length || 0

  // While the game is frozen waiting on someone else's card/buy choice (a
  // double rent is decided by the property owner, so this can happen on our own
  // turn) nothing here is actionable — rolling, building and ending the turn
  // are all blocked server-side. Leave only Trade, which is turn-independent.
  const waitingOnOtherPlayersDecision =
    !!pendingDecision && pendingDecision.playerOrder !== myOrder
  const isMyTurn = myGame?.situation?.turn === getUserCreds().email
  const showTurnButtons = isMyTurn && !waitingOnOtherPlayersDecision

  // Leave build mode automatically once it is no longer our turn.
  useEffect(() => {
    if (
      houseBuildMode &&
      myGame?.situation?.turn !== getUserCreds().email
    ) {
      dispatch(setState({ houseBuildMode: null }))
    }
  }, [houseBuildMode, myGame?.situation?.turn, dispatch])

  const onClickOnRoll = () => {
    if (!myGame?.situation) return
    dispatch(setState({ diceRolling: true }))
    const activePlayer = myGame?.situation?.players.find(
      (player) => player.id === myGame?.situation?.turn
    )
    if (activePlayer?.inJail) {
      const randomRoll = evaluateRequiredRollValues(diceInputVal)
      socket.emit("game.diceRoll", myGame?.id || "", randomRoll, true)
      return
    }
    const randomRoll = evaluateRequiredRollValues(diceInputVal)
    socket.emit("game.diceRoll", myGame?.id || "", randomRoll)
  }

  const diceRollEvent = myGame?.situation?.events.find(
    (event) => event.type === "dice-roll"
  )
  const shouldHideRollButton = useMemo(() => {
    const activePlayer = myGame?.situation?.players.find(
      (player) => player.id === myGame?.situation?.turn
    )
    if (activePlayer?.money && activePlayer.money < 0) return true
    if (myGame?.situation?.activeAuctionPropertyOrder !== null)
      return true
    if (diceRolling) return true
    if (pieceMovementAnimationRunning) return true
    if (myGame?.situation?.turn !== getUserCreds().email) return true
    if (myGame.situation.rollEnabled === false) return true
    if (
      myGame.situation.events.find(
        (event) => event.type === "piece-movement"
      )
    )
      return true
    if (modalData.buyOrAuction?.visible) return true
  }, [
    myGame,
    modalData.buyOrAuction,
    diceRollEvent,
    diceRolling,
    pieceMovementAnimationRunning,
  ])
  const player = myGame?.situation?.players.find(
    (player) => player.id === myGame?.situation?.turn
  )

  const isBankrupt = (player?.money || 1) < 0
  const isRollingSecondTime = player?.rollHistory.length !== 0
  const isConstructionBanned =
    myGame?.situation?.constructionBanned ||
    myGame?.challengeId === "win-as-rent-lord-without-houses"

  // Buy houses requires owning at least one completed (build-eligible) set.
  const hasCompleteSet =
    !!myGame &&
    myOrder !== undefined &&
    playerHasCompleteSet(myGame, myOrder as PlayerOrder)
  // Sell houses requires having at least one house on a property.
  const hasAnyHouses =
    !!myGame &&
    myOrder !== undefined &&
    playerHasAnyHouses(myGame, myOrder as PlayerOrder)

  const isBuyHousesDisabled = isConstructionBanned || !hasCompleteSet
  const buyHousesTooltip = !hasCompleteSet
    ? "Complete at least one set to buy houses"
    : undefined
  const isSellHousesDisabled = !hasAnyHouses
  const sellHousesTooltip = !hasAnyHouses
    ? "You don't have any houses to sell"
    : undefined

  let color = "#ba711e"
  const handleCardClick = () => {
    // if (getUserCreds().email !== player.id) return
    if (myOrder !== undefined) {
      dispatch(
        setModalData({
          playerCards: { visible: true, playerOrder: myOrder },
        })
      )
    }
  }
  const isEndTurnDisabled =
    isBankrupt ||
    !myGame?.situation?.endTurnEnabled ||
    (disableEndTurnLocally &&
      myGame?.situation?.players.length !== 1)
  // Idle auto-roll/auto-end lives in useIdleAutoAction, mounted at the game
  // root — it needs
  // window-level input listeners and has to keep running even where these
  // buttons aren't rendered.
  if (myGame?.situation?.gameOver) return null
  // if (myGame?.situation?.gameOver) return null

  const mainButtonSize = isMobile() ? "base" : "base"
  return (
    <div
      className={classNames(
        "flex  flex-wrap justify-center gap-4 sm:gap-2  w-full px-4",
        {}
      )}
    >
      {showTurnButtons && (
        <MainButton
          baseColor="#d48e3d"
          size={mainButtonSize}
          disabled={shouldHideRollButton}
          onClick={onClickOnRoll}
          className={
            shouldHideRollButton
              ? "grayscale opacity-60 cursor-not-allowed"
              : ""
          }
          data-testid="game.roll-btn"
        >
          <div className="flex flex-row items-center justify-center gap-3 sm:gap-2 w-full">
            <i className="fa-dice fa-solid text-white fa-xl sm:!text-base"></i>
            <span className="text-base sm:text-sm whitespace-nowrap text-white/90 font-poppins font-semibold tracking-wide uppercase">
              Roll
            </span>
          </div>
        </MainButton>
      )}

      {/* Trade is available to anyone at any time, not just on your turn, so it
          no longer belongs with these turn buttons — it lives in the left
          sidebar above the bank (CreateTradeButton). */}

      {showTurnButtons && (
        <span title={buyHousesTooltip} className="inline-flex">
          <MainButton
            baseColor="#2f9e44"
            size={mainButtonSize}
            onClick={() => enterHouseBuildMode("buy")}
            className={
              isBuyHousesDisabled
                ? "grayscale opacity-60 cursor-not-allowed"
                : ""
            }
            disabled={isBuyHousesDisabled}
            data-testid="game.buy-houses-btn"
          >
            <div className="flex flex-row items-center justify-center gap-3 sm:gap-2 w-full">
              <i className="fa-house fa-solid text-white fa-xl sm:!text-base"></i>
              <span className="text-base sm:text-sm whitespace-nowrap text-white/90 font-poppins font-semibold tracking-wide uppercase">
                Buy houses
              </span>
            </div>
          </MainButton>
        </span>
      )}

      {showTurnButtons && (
        <span title={sellHousesTooltip} className="inline-flex">
          <MainButton
            baseColor="#b6a84b"
            size={mainButtonSize}
            onClick={() => enterHouseBuildMode("sell")}
            className={
              isSellHousesDisabled
                ? "grayscale opacity-60 cursor-not-allowed"
                : ""
            }
            disabled={isSellHousesDisabled}
            data-testid="game.sell-houses-btn"
          >
            <div className="flex flex-row items-center justify-center gap-3 sm:gap-2 w-full">
              <i className="fa-house-circle-xmark fa-solid text-white fa-xl sm:!text-base"></i>
              <span className="text-base sm:text-sm whitespace-nowrap text-white/90 font-poppins font-semibold tracking-wide uppercase">
                Sell houses
              </span>
            </div>
          </MainButton>
        </span>
      )}

      {/* The cards button is the one turn button that stays up off-turn — you
          can always look through your hand, you just can't spend it, so only
          the label changes. */}
      {myOrder !== undefined && (
        <MainButton
          baseColor="#8b5cf6"
          size={mainButtonSize}
          onClick={handleCardClick}
          disabled={false}
          data-testid="game.cards-btn"
        >
          <div className="flex flex-row items-center justify-center gap-3 sm:gap-2 w-full">
            <img className="object-contain w-10 sm:w-7" src={images.icons.cards}></img>
            <span className="text-base sm:text-sm whitespace-nowrap text-white/90 font-poppins font-semibold tracking-wide uppercase">
              {showTurnButtons ? "Play cards" : "See your cards"}
            </span>
            <span
              className="min-w-[1.5rem] sm:min-w-[1.25rem] rounded-full bg-black/25 px-2 py-1 sm:px-1.5 sm:py-0.5 text-sm sm:text-xs text-white font-poppins font-semibold leading-none text-center"
              data-testid="game.cards-btn-count"
            >
              {myCardsCount}
            </span>
          </div>
        </MainButton>
      )}

      <div
        className={classNames(
          "flex items-center gap-2 fixed -translate-x-1/2 -top-10 left-1/2",
          {
            hidden: cheats === false,
          }
        )}
      >
        <input
          type="text"
          data-testid="game.dice-input"
          onChange={(e) => setDiceInputVal(e.target.value)}
        />
        <button
          onClick={() => {
            socket.emit("game.pause-timer", myGame?.id || "")
          }}
        >
          Pause
        </button>
        <button
          onClick={() => {
            socket.emit("game.resume-timer", myGame?.id || "")
          }}
        >
          Resume
        </button>
      </div>

      {/* End Turn button in second row */}
      <div className={classNames("col-span-4 row-start-2", { hidden: !showTurnButtons })}>
        {showTurnButtons && (
          <MainButton
            baseColor="#c45a3d"
            size={mainButtonSize}
            disabled={isEndTurnDisabled}
            className={
              isEndTurnDisabled ? "grayscale opacity-60 cursor-not-allowed" : ""
            }
            onClick={handleEndTurn}
            data-testid="game.end-turn-btn"
          >
            <div className="flex flex-row items-center justify-center gap-3 sm:gap-2 w-full">
              <i className="fa-rotate-right fa-solid text-white fa-xl sm:!text-base"></i>
              <span className="text-base sm:text-sm whitespace-nowrap text-white/90 font-poppins font-semibold tracking-wide uppercase">
                End Turn
              </span>
            </div>
          </MainButton>
        )}
      </div>
    </div>
  )
}
export default GameButtons
